// Sun Clip State Manager
// Converted from ES6 module to IIFE for Shopify compatibility
(function() {
    'use strict';

    window.SunClipStateManager = function() {
        this.storageKey = 'chemistrie_sunclip_state';
        this.configHistoryKey = 'chemistrie_sunclip_saved_configurations';
        this.maxHistoryItems = 10;

        return this;
    };

    window.SunClipStateManager.prototype = {
        // Save current configuration state
        saveState: function(configuration) {
            try {
                const stateData = {
                    configuration,
                    timestamp: Date.now(),
                    version: '1.0'
                };

                localStorage.setItem(this.storageKey, JSON.stringify(stateData));

                // Dispatch event for other components
                window.dispatchEvent(new CustomEvent('configurationSaved', {
                    detail: { configuration }
                }));

            } catch (error) {
                console.error('Error saving state:', error);
                // Fallback to session storage
                try {
                    sessionStorage.setItem(this.storageKey, JSON.stringify({ configuration }));
                } catch (sessionError) {
                    console.error('Error saving to session storage:', sessionError);
                }
            }
        },

        // Load saved configuration state
        loadState: function() {
            try {
                // Try localStorage first
                const savedData = localStorage.getItem(this.storageKey);
                if (savedData) {
                    const parsed = JSON.parse(savedData);

                    // Check if data is recent (within 24 hours)
                    const dayAgo = Date.now() - (24 * 60 * 60 * 1000);
                    if (parsed.timestamp && parsed.timestamp > dayAgo) {
                        return parsed.configuration;
                    }
                }

                // Try session storage as fallback
                const sessionData = sessionStorage.getItem(this.storageKey);
                if (sessionData) {
                    return JSON.parse(sessionData).configuration;
                }

            } catch (error) {
                console.error('Error loading state:', error);
            }

            return null;
        },

        // Clear current state
        clearState: function() {
            try {
                localStorage.removeItem(this.storageKey);
                sessionStorage.removeItem(this.storageKey);

                window.dispatchEvent(new CustomEvent('configurationCleared'));
            } catch (error) {
                console.error('Error clearing state:', error);
            }
        },

        // Save a completed configuration to history
        saveConfiguration: function(configuration, name) {
            try {
                const savedConfigs = this.getSavedConfigurations();

                const configData = {
                    id: this.generateConfigId(),
                    name: name || `Configuration ${savedConfigs.length + 1}`,
                    configuration,
                    timestamp: Date.now(),
                    previewImage: null // Will be set externally
                };

                savedConfigs.unshift(configData);

                // Keep only the latest configurations
                if (savedConfigs.length > this.maxHistoryItems) {
                    savedConfigs.splice(this.maxHistoryItems);
                }

                localStorage.setItem(this.configHistoryKey, JSON.stringify(savedConfigs));

                window.dispatchEvent(new CustomEvent('configurationHistoryUpdated', {
                    detail: { configurations: savedConfigs }
                }));

                return configData.id;

            } catch (error) {
                console.error('Error saving configuration:', error);
                return null;
            }
        },

        // Get all saved configurations
        getSavedConfigurations: function() {
            try {
                const saved = localStorage.getItem(this.configHistoryKey);
                return saved ? JSON.parse(saved) : [];
            } catch (error) {
                console.error('Error loading saved configurations:', error);
                return [];
            }
        },

        // Load a specific saved configuration
        loadConfiguration: function(configId) {
            try {
                const savedConfigs = this.getSavedConfigurations();
                const config = savedConfigs.find(c => c.id === configId);

                if (config) {
                    this.saveState(config.configuration);

                    window.dispatchEvent(new CustomEvent('configurationLoaded', {
                        detail: { configuration: config.configuration }
                    }));

                    return config.configuration;
                }
            } catch (error) {
                console.error('Error loading configuration:', error);
            }

            return null;
        },

        // Delete a saved configuration
        deleteConfiguration: function(configId) {
            try {
                const savedConfigs = this.getSavedConfigurations();
                const updatedConfigs = savedConfigs.filter(c => c.id !== configId);

                localStorage.setItem(this.configHistoryKey, JSON.stringify(updatedConfigs));

                window.dispatchEvent(new CustomEvent('configurationDeleted', {
                    detail: { configId, configurations: updatedConfigs }
                }));

                return true;
            } catch (error) {
                console.error('Error deleting configuration:', error);
                return false;
            }
        },

        // Update preview image for a saved configuration
        updatePreviewImage: function(configId, previewImage) {
            try {
                const savedConfigs = this.getSavedConfigurations();
                const config = savedConfigs.find(c => c.id === configId);

                if (config) {
                    config.previewImage = previewImage;
                    localStorage.setItem(this.configHistoryKey, JSON.stringify(savedConfigs));
                    return true;
                }
            } catch (error) {
                console.error('Error updating preview image:', error);
            }

            return false;
        },

        // Generate shareable configuration URL
        generateShareableUrl: function(configuration) {
            try {
                const configData = {
                    v: '1.0', // version
                    f: configuration.frameModel,
                    l: configuration.lensColor,
                    lc: configuration.lensCategory,
                    b: configuration.bridgeColor,
                    m: configuration.magnetColor,
                    c: configuration.crystalColor
                };

                // Encode configuration as URL parameters
                const params = new URLSearchParams();
                Object.entries(configData).forEach(([key, value]) => {
                    if (value && value !== 'null') params.set(key, value);
                });

                const baseUrl = window.location.origin + window.location.pathname;
                return `${baseUrl}?config=${btoa(params.toString())}`;

            } catch (error) {
                console.error('Error generating shareable URL:', error);
                return null;
            }
        },

        // Load configuration from URL parameters
        loadFromUrl: function() {
            try {
                const urlParams = new URLSearchParams(window.location.search);
                const configParam = urlParams.get('config');

                if (!configParam) return null;

                const decodedParams = new URLSearchParams(atob(configParam));

                const configuration = {
                    frameModel: decodedParams.get('f') || 'Raen-Angle',
                    lensColor: decodedParams.get('l'),
                    lensCategory: decodedParams.get('lc'),
                    bridgeColor: decodedParams.get('b'),
                    magnetColor: decodedParams.get('m'),
                    crystalColor: decodedParams.get('c') === 'null' ? null : decodedParams.get('c')
                };

                // Validate configuration
                if (this.isValidConfiguration(configuration)) {
                    return configuration;
                }
            } catch (error) {
                console.error('Error loading configuration from URL:', error);
            }

            return null;
        },

        // Validate configuration object
        isValidConfiguration: function(config) {
            // Basic validation
            return config &&
                   typeof config === 'object' &&
                   config.frameModel &&
                   (config.lensColor || config.bridgeColor || config.magnetColor);
        },

        // Generate unique configuration ID
        generateConfigId: function() {
            return `config_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        },

        // Export configuration as JSON
        exportConfiguration: function(configId) {
            try {
                let configData;

                if (configId) {
                    const savedConfigs = this.getSavedConfigurations();
                    configData = savedConfigs.find(c => c.id === configId);
                } else {
                    // Export current state
                    const currentConfig = this.loadState();
                    if (currentConfig) {
                        configData = {
                            id: this.generateConfigId(),
                            name: 'Current Configuration',
                            configuration: currentConfig,
                            timestamp: Date.now()
                        };
                    }
                }

                if (configData) {
                    const exportData = {
                        version: '1.0',
                        exported: new Date().toISOString(),
                        ...configData
                    };

                    return JSON.stringify(exportData, null, 2);
                }
            } catch (error) {
                console.error('Error exporting configuration:', error);
            }

            return null;
        },

        // Import configuration from JSON
        importConfiguration: function(jsonString) {
            try {
                const importedData = JSON.parse(jsonString);

                // Validate imported data
                if (importedData.version && importedData.configuration) {
                    const configuration = importedData.configuration;

                    if (this.isValidConfiguration(configuration)) {
                        // Save as new configuration
                        const configId = this.saveConfiguration(
                            configuration,
                            importedData.name || 'Imported Configuration'
                        );

                        return { success: true, configId };
                    }
                }
            } catch (error) {
                console.error('Error importing configuration:', error);
            }

            return { success: false, error: 'Invalid configuration format' };
        },

        // Get storage usage information
        getStorageInfo: function() {
            try {
                const stateSize = localStorage.getItem(this.storageKey)?.length || 0;
                const historySize = localStorage.getItem(this.configHistoryKey)?.length || 0;
                const totalSize = stateSize + historySize;

                return {
                    currentStateSize: stateSize,
                    historySize: historySize,
                    totalSize: totalSize,
                    maxHistoryItems: this.maxHistoryItems,
                    savedCount: this.getSavedConfigurations().length
                };
            } catch (error) {
                console.error('Error getting storage info:', error);
                return null;
            }
        },

        // Clean up old data
        cleanupStorage: function() {
            try {
                // Remove expired states
                const savedData = localStorage.getItem(this.storageKey);
                if (savedData) {
                    const parsed = JSON.parse(savedData);
                    const weekAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);

                    if (parsed.timestamp && parsed.timestamp < weekAgo) {
                        localStorage.removeItem(this.storageKey);
                    }
                }

                // Clean up old configurations
                const savedConfigs = this.getSavedConfigurations();
                const monthAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);

                const recentConfigs = savedConfigs.filter(config =>
                    config.timestamp > monthAgo
                );

                if (recentConfigs.length !== savedConfigs.length) {
                    localStorage.setItem(this.configHistoryKey, JSON.stringify(recentConfigs));
                }

                return true;
            } catch (error) {
                console.error('Error cleaning up storage:', error);
                return false;
            }
        }
    };

    // Compatibility aliases
    window.StateManager = window.SunClipStateManager;  // Legacy alias
    window.ConfiguratorState = window.SunClipStateManager;  // New generic name (renamed from sun-clip-state-manager.js)

})();